import { action, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosProgressEvent, AxiosResponse, CancelTokenSource } from 'axios';

import DetailsActivities from 'ui/stores/DetailsActivities';

import { ActivityListResponse, ActivityResponse } from 'ui/types';
import { ActivityType } from 'utils/stores/Activities/constants';

import { PaginationParams } from 'utils/api/types';
import Logger from 'utils/logger';

import { EventType } from 'utils/events/constants';
import { ActionEvent, CreatedActivityPayload } from 'utils/events/types';
import API from 'utils/api';
import { postMessage } from 'utils/events/broadcast';
import RootStore from 'stores/Root';

export default class AlarmActivities extends DetailsActivities<RootStore, typeof API> {
  alarmId: string;

  constructor(rootStore: RootStore, api: typeof API, alarmId: string) {
    super(rootStore, api);

    makeObservable(this);

    this.alarmId = alarmId;

    eventBus.register(this);
  }

  async loadActivitiesData(params: PaginationParams): Promise<AxiosResponse<ActivityListResponse>> {
    return this.api.alarms.loadAlarmActivities(this.alarmId, params)();
  }

  @action.bound
  async addMessage(message: string): Promise<void> {
    if (this.error || !this.activities) {
      return;
    }
    try {
      const { data } = await this.api.alarms.addAlarmMessage(this.alarmId, message)();
      runInAction(() => {
        if (this.activities) {
          this.activities = [...this.activities, { ...data, type: ActivityType.Message }];
          postMessage(EventType.AddedAlarmMessage, {
            ...data,
            alarmId: this.alarmId,
          });
        }
      });
    } catch (e) {
      Logger.error(`Invalid add message API result: Alarm ID: ${this.alarmId}. Message: ${message}`, e);
    }
  }

  @action.bound
  async updateMessage(messageId: string, newMessage: string): Promise<void> {
    if (this.activities === undefined) {
      return;
    }
    try {
      const { data } = await this.api.alarms.updateAlarmMessage(this.alarmId, messageId, newMessage)();
      runInAction(() => {
        const messageIndex = this.activities?.findIndex((activity) => activity.uuid === messageId);
        if (messageIndex !== undefined && messageIndex >= 0 && this.activities) {
          this.activities[messageIndex] = {
            ...data,
            type: ActivityType.Message,
          };
        }
      });
    } catch (e) {
      Logger.error(`Invalid update message API result: Alarm ID: ${this.alarmId}. Message ID: ${messageId}`, e);
    }
  }

  @action.bound
  async deleteMessage(messageId: string): Promise<void> {
    if (this.activities === undefined) {
      return;
    }
    const message = this.activities.find((activity) => activity.uuid === messageId);
    if (message) {
      try {
        await this.api.alarms.removeAlarmMessage(this.alarmId, messageId)();
        runInAction(() => {
          if (this.activities !== undefined) {
            this.activities = this.activities.filter((activity) => activity.uuid !== messageId);
          }
          postMessage(EventType.DeletedAlarmMessage, {
            alarmId: this.alarmId,
            messageId,
          });
        });
      } catch (e) {
        this.rootStore.notification.enqueueErrorSnackbar(
          'Unable to delete the message at this time. Please try again later'
        );
        Logger.error(`Invalid delete message API result: Alarm ID: ${this.alarmId}. Message ID: ${messageId}`, e);
      }
    }
  }

  async doUploadFile(
    file: File,
    onUpdateProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): Promise<void> {
    try {
      const { data } = await this.api.alarms.uploadAlarmFile(this.alarmId, file, onUpdateProgress, cancelTokenSource)();
      runInAction(() => {
        if (this.activities) {
          this.activities = [...this.activities, data as unknown as ActivityResponse];
        }
      });
    } catch (e) {
      Logger.error(`Invalid add file API result: Task ID: ${this.alarmId}.`, e);
      this.rootStore.notification.enqueueErrorSnackbar('Failed to upload file');
    }
  }

  @action.bound
  async deleteFile(fileId: string): Promise<void> {
    if (this.activities === undefined) {
      return;
    }
    const file = this.activities.find((activity) => activity.uuid === fileId);
    if (file) {
      try {
        await this.api.alarms.removeAlarmFile(this.alarmId, fileId)();
        runInAction(() => {
          this.removeActivity(file.uuid);
        });
      } catch (e) {
        this.rootStore.notification.enqueueErrorSnackbar(
          'Unable to delete the file at this time. Please try again later'
        );
        Logger.error(`Invalid delete file API result: Task ID: ${this.alarmId}. File ID: ${fileId}`, e);
      }
    }
  }

  @subscribe(EventType.CreatedActivity)
  @action
  createdActivity({ payload }: ActionEvent<CreatedActivityPayload>): void {
    if (this.error || !this.activities || !payload.alarm || payload.alarm.uuid !== this.alarmId) {
      return;
    }

    this.activities = undefined;
    this.loadActivities();
  }
}
